import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { UserService } from 'src/app/api/services';
import { MESSAGES } from 'src/app/core/constant/messages';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { NavButtonComponent } from 'src/app/shared/component/nav-button/nav-button.component';
import { RegisterUserDto } from 'src/app/shared/model/auth';
import { ToastService } from 'src/app/shared/service/toast.service';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { InputDateComponent } from 'src/app/shared/component/input-date/input-date.component';
import { dateRangeValidator } from 'src/app/shared/validator/dateRangeValidator';
import { AuthService } from 'src/app/shared/service/auth.service';
import { catchError, concatMap, finalize, Observable, throwError } from 'rxjs';

declare const grecaptcha: any;

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    LoadingButtonComponent,
    InputTextComponent,
    InputSelectComponent,
    NavButtonComponent,
    InputDateComponent,
  ],
})
export class RegisterComponent extends BaseFormComponent {
  form: FormGroup;
  loading: boolean = false;

  sexOptions = [
    { value: 'Masculino', label: 'Masculino' },
    { value: 'Femenino', label: 'Femenino' },
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    toastService: ToastService
  ) {
    super(toastService);
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      birthDate: ['', [Validators.required, dateRangeValidator]],
      sex: ['', Validators.required],
      identificationDocument: [
        '',
        [Validators.required, Validators.pattern(/^\d{8}$/)],
      ],
      telfOne: ['', [Validators.required, Validators.pattern(/^9\d{8}$/)]],
    });
  }

  onSubmit(): void {
    if (!this.validateForm(this.form)) return;

    this.loading = true;

    grecaptcha.ready(() => {
      grecaptcha
        .execute('6LcUwHMaAAAAAIUn-5HQ25qU1FhSR4zUVoGmqOvt', {
          action: 'register',
        })
        .then((registerToken: string) => this.registerUser(registerToken))
        .catch(() => this.handleRecaptchaError());
    });
  }

  private registerUser(registerToken: string): void {
    const formValue = this.form.value;

    const newUser: RegisterUserDto & { 'g-recaptcha-response': string } = {
      ...formValue,
      birthDate: this.toBackendDate(formValue.birthDate) ?? '',
      'g-recaptcha-response': registerToken,
    };

    this.authService
      .register(newUser)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => this.handleRegisterSuccess(),
        error: (error) => this.handleRegisterError(error),
      });
  }

  private handleRegisterSuccess(): void {
    const email = this.form.value.email;
    localStorage.setItem('emailCodijobsRegister', email);
    this.router.navigate(['/confirmation']);
  }

  private handleRegisterError(error: any): void {
    const messages = error?.error?.listMessage;
    this.toastService.showMessages('error', [messages]);
  }

  private handleRecaptchaError(): void {
    this.loading = false;
    this.toastService.showMessages('error', [
      'No se pudo validar el reCAPTCHA. Intenta nuevamente.',
    ]);
  }

  private toBackendDate(value: string): string | null {
    if (!value) return null;
    const [d, m, y] = value.split('/');
    return `${y}-${m}-${d}`;
  }
}
